/**
 * Application Entry Point & SPA Router
 */

import './styles/main.css';
import './styles/matrix.css';
import './styles/header.css';
import './styles/terminal.css';
import './styles/cards.css';
import './styles/pages.css';
import './styles/auth.css';
import './styles/admin.css';

import { MatrixCanvas } from './components/MatrixCanvas.js';
import { Navbar } from './components/Navbar.js';
import { HomePage } from './pages/HomePage.js';
import { BlogPage } from './pages/BlogPage.js';
import { FilesPage } from './pages/FilesPage.js';
import { GalleryPage } from './pages/GalleryPage.js';
import { AboutPage } from './pages/AboutPage.js';
import { authService } from './services/authService.js';
import { RecoveryPage, readAccountLink } from './pages/RecoveryPage.js';
import { SecurityPage } from './pages/SecurityPage.js';
import { AdminContentPage } from './pages/AdminContentPage.js';
import { getLanguage, localize, observeLocalization } from './services/i18n.js';
class App {
  constructor() {
    this.appEl = document.getElementById('app');
    this.matrix = null;
    this.navbar = null;
    this.currentPage = null;
  }

  init() {
    document.documentElement.lang = getLanguage();
    localize(document.body);
    this.stopLocalization = observeLocalization(document.body);
    // 1. Initialize Matrix background
    this.matrix = new MatrixCanvas('matrix-canvas');

    // 2. Initialize Navbar with navigation callback & matrix toggle
    this.navbar = new Navbar(
      (route, category) => this.navigateTo(route, category),
      () => this.matrix.toggle()
    );

    // 3. Listen to browser hash changes
    window.addEventListener('hashchange', () => this.handleRouteFromHash());
    window.addEventListener('fixlan:language_change', () => { localize(document.body); void this.handleRouteFromHash(); });

    // 4. Initial route load
    this.handleRouteFromHash();
    void authService.refresh();
    window.addEventListener('focus', () => { void authService.refresh(); });
    window.addEventListener('fixlan:auth_change', () => {
      if (window.location.hash.split('/')[0] === '#admin') {
        if (this.currentPage?.onAuthChange) this.currentPage.onAuthChange();
        else void this.handleRouteFromHash();
      }
    });
  }

  navigateTo(route, category = null) {
    let hash = `#${route}`;
    if (category && category !== 'all') {
      hash += `/${category}`;
    }
    window.location.hash = hash;
  }

  async handleRouteFromHash() {
    const account = readAccountLink(window.location.hash, hash => window.history.replaceState(null, '', hash));
    const rawHash = window.location.hash.replace(/^#\/?/, '') || 'home';
    const parts = rawHash.split('/');
    const route = parts[0] || 'home';
    const category = parts[1] || null;

    // Update Header active state
    this.navbar.setRoute(route, category);

    // Render page view
    await this.renderRoute(route, category, account, parts.slice(1).join('/'));
  }

  async renderRoute(route, category, account, routeSuffix = '') {
    this.currentPage?.dispose?.();
    const renderVersion = this.renderVersion = (this.renderVersion || 0) + 1;
    this.appEl.innerHTML = '';

    // Always render Navbar at top
    const headerEl = this.navbar.render();
    this.appEl.appendChild(headerEl);

    // Main page container
    const mainContent = document.createElement('main');
    mainContent.id = 'main-content';

    let pageComponent = null;

    switch (route) {
      case 'security':
        await authService.refresh();
        pageComponent = new SecurityPage();
        mainContent.appendChild(pageComponent.render());
        break;
      case 'recover-mfa':
      case 'forgot-password':
      case 'resend-verification':
      case 'verify-email':
      case 'reset-password':
      case 'change-password':
        if (route === 'change-password') await authService.refresh();
        pageComponent = new RecoveryPage(route, account?.token);
        mainContent.appendChild(pageComponent.render());
        break;
      case 'home':
        pageComponent = new HomePage(
          (r, c) => this.navigateTo(r, c),
          () => this.matrix.toggle()
        );
        mainContent.appendChild(pageComponent.render());
        break;

      case 'blog':
        pageComponent = new BlogPage((r, c) => this.navigateTo(r, c), category);
        mainContent.appendChild(await pageComponent.render());
        break;

      case 'files':
        pageComponent = new FilesPage((r, c) => this.navigateTo(r, c), routeSuffix || 'all');
        mainContent.appendChild(await pageComponent.render());
        break;

      case 'gallery':
        pageComponent = new GalleryPage((r, c) => this.navigateTo(r, c), undefined, routeSuffix);
        mainContent.appendChild(await pageComponent.render());
        break;

      case 'about':
        pageComponent = new AboutPage((r, c) => this.navigateTo(r, c));
        mainContent.appendChild(pageComponent.render());
        break;

      case 'admin':
        pageComponent = new AdminContentPage();
        pageComponent.initialView = category;
        mainContent.appendChild(pageComponent.render());
        break;

      default:
        // Fallback to Home
        pageComponent = new HomePage(
          (r, c) => this.navigateTo(r, c),
          () => this.matrix.toggle()
        );
        mainContent.appendChild(pageComponent.render());
    }

    if (renderVersion !== this.renderVersion) { pageComponent?.dispose?.(); return; }
    this.currentPage = pageComponent;
    this.appEl.appendChild(localize(mainContent));

    // Footer
    const footer = document.createElement('footer');
    footer.style.cssText = `
      margin-top: auto;
      padding: 24px 0;
      border-top: 1px dashed var(--neon-green-border);
      text-align: center;
      font-size: 0.8rem;
      color: var(--text-dim);
    `;
    footer.innerHTML = `
      <div class="container" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;">
        <div>FixLan // Исследования безопасности и разработка</div>
        <div>fixlan.ru ~ &copy; ${new Date().getFullYear()}</div>
      </div>
    `;
    this.appEl.appendChild(localize(footer));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

// Bootstrap Application
document.addEventListener('DOMContentLoaded', () => {
  const app = new App();
  app.init();
});
